import SEOHead from "@/components/SEOHead";
import FaqAccordion from "@/components/FaqAccordion";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ScoreInlineForm from "@/components/ScoreInlineForm";
import Breadcrumbs from "@/components/Breadcrumbs";
import SchemaScript from "@/components/SchemaScript";
import { Link } from "wouter";
import { ArrowRight, CheckCircle2, Globe, Search, Shield } from "lucide-react";
import { trackSectionCtaClick } from "@/lib/analytics";

const pageDescription =
  "AI website design in London for B2B and B2C brands: answer-first pages and clean structured data. AI-Ready from £3,500 including VAT until 30 Nov 2026.";

const answerBlock =
  "An AI website design agency builds sites that people and AI assistants can both read: answer-first pages, clean structured data, fast loading and content that crawlers can reach. At Integrated.Social we build with AI agents, including Manus and Grok. Agents draft; a named senior person signs off every page before it goes live. It suits B2B and B2C teams replacing a brochure site; start with the Free AI Visibility Score or ask for a fixed-price proposal.";

const faqSummary =
  "These questions cover what an AI website build includes, how it differs from a standard site, which AI tools we use and how humans stay accountable, what it costs, how timelines are set, whether a build can win AI citations, and whether we rebuild existing sites for B2B, B2C and ecommerce brands. The short version: a well-built, reviewed site improves readiness for search and AI answers, but nobody can promise citations.";

const faqs = [
  {
    q: "What does an AI website design agency do?",
    a: "An AI website design agency builds sites that people and AI assistants can both read: answer-first pages, clean structured data, fast loading and content that crawlers can reach. At Integrated.Social we build with AI agents, including Manus and Grok. Agents draft; a named senior person signs off every page before it goes live.",
  },
  {
    q: "How is an AI website different from a regular website?",
    a: "An AI website is built so assistants and search engines can read, trust, and quote it, not only so people can browse it. That means answer-first pages, consistent structured data, and server-rendered content. A regular site can look identical but hide its answers behind scripts or vague copy. Our guide on AI websites versus regular websites covers the differences in detail.",
  },
  {
    q: "Do you build websites with Manus and Grok?",
    a: "Yes. We use AI agents, including Manus and Grok, to research, draft copy, write components, and produce structured data. They are tools we use, not partners. Agents draft; a named senior person signs off the content, claims, and release before anything goes live.",
  },
  {
    q: "How much does an AI website cost?",
    a: "AI-Ready builds start from £3,500 including VAT for up to 8 pages, a special offer until 30 November 2026. AI Website Visibility (AEO) starts from £18,500 including VAT for up to 20 pages with a blog system, and Enterprise AI from £38,000 including VAT for larger content systems. We confirm a fixed price after a 30-minute discovery call. If you want a baseline first, start with the Free AI Visibility Score.",
  },
  {
    q: "How long does an AI website build take?",
    a: "It depends on scope, how ready your content is, and how quickly approvals happen, so we set the timeline in the fixed proposal rather than promise one up front. Smaller builds move faster than sites with integrations or many templates.",
  },
  {
    q: "Will an AI website get us cited by ChatGPT or Google AI Overviews?",
    a: "No one can promise that, and you should be wary of anyone who does. Google says its AI search features need no special AI markup or new machine-readable files. What a good build controls is whether your pages are accessible, clear, and backed by evidence worth citing. We then test a fixed set of buyer questions over time.",
  },
  {
    q: "Can you rebuild our existing website rather than start from scratch?",
    a: "Yes. Many projects start with an audit of the current site, then keep what works and rebuild the pages that block crawlers or bury answers. Our migration checklist explains the steps.",
  },
  {
    q: "Do you build AI websites for B2C and ecommerce brands?",
    a: "Yes. We build for B2B, B2C, and ecommerce brands in the UK and US. For online stores we pair the build with our AI marketing work for ecommerce, covering product pages, feeds, and AI shopping visibility.",
  },
];

const pricingTiers = [
  {
    name: "AI-Ready",
    price: "From £3,500 including VAT",
    note: "Special offer until 30 November 2026",
    description:
      "For a focused site with up to 8 pages and a reliable technical foundation.",
    features: [
      "Up to 8 pages",
      "Full structured data implementation",
      "Answer-first architecture",
      "Technical SEO foundation",
      "Analytics setup",
      "30 days of post-launch support",
    ],
    tier: "ai-ready",
  },
  {
    name: "AI Website Visibility (AEO)",
    price: "From £18,500 including VAT",
    description:
      "For teams that need up to 20 pages plus a blog system, built so people and AI assistants can both read them.",
    features: [
      "Up to 20 pages plus a blog system",
      "Structured data and entity foundations",
      "CRM and marketing automation integration",
      "Conversion-rate work",
      "Analytics and attribution setup",
      "Human review before release",
    ],
    tier: "ai-website-visibility-aeo",
  },
  {
    name: "Enterprise AI",
    price: "From £38,000 including VAT",
    description:
      "For larger content systems, with agent-assisted content workflows and human review.",
    features: [
      "Larger content system",
      "Agent-assisted content workflows",
      "Multi-persona conversion paths",
      "Analytics and attribution",
      "Human review before release",
      "Quarterly strategy reviews",
    ],
    tier: "enterprise-ai",
  },
];

const aiWebsitesSchema = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebPage",
      "@id": "https://integrated.social/services/ai-websites/#webpage",
      url: "https://integrated.social/services/ai-websites/",
      name: "AI Website Design Agency London | AEO and SEO-Ready Sites",
      headline: "AI Website Design Agency in London for B2B and B2C Brands",
      description: pageDescription,
      abstract: answerBlock,
      inLanguage: "en-US",
      isPartOf: { "@id": "https://integrated.social/#website" },
      about: {
        "@id": "https://integrated.social/services/ai-websites/#service",
      },
      publisher: { "@id": "https://integrated.social/#organization" },
      breadcrumb: {
        "@id": "https://integrated.social/services/ai-websites/#breadcrumb",
      },
    },
    {
      "@type": "Service",
      "@id": "https://integrated.social/services/ai-websites/#service",
      name: "AI Website Design and Development",
      serviceType: "AI website design",
      description:
        "AI website design in London for B2B and B2C brands: answer-first pages, clean structured data, and AI-agent builds, signed off by a named senior person.",
      provider: { "@id": "https://integrated.social/#organization" },
      areaServed: [
        { "@type": "Country", name: "United Kingdom" },
        { "@type": "Country", name: "United States" },
      ],
      url: "https://integrated.social/services/ai-websites/",
      audience: [
        { "@type": "BusinessAudience", audienceType: "B2B companies" },
        { "@type": "Audience", audienceType: "B2C and ecommerce brands" },
      ],
      offers: [
        {
          "@type": "Offer",
          "@id":
            "https://integrated.social/services/ai-websites/#offer-ai-ready",
          name: "AI-Ready",
          price: "3500",
          priceCurrency: "GBP",
          priceValidUntil: "2026-11-30",
          validThrough: "2026-11-30",
          priceSpecification: {
            "@type": "UnitPriceSpecification",
            price: "3500",
            priceCurrency: "GBP",
            valueAddedTaxIncluded: true,
            validThrough: "2026-11-30",
          },
          description:
            "AI-Ready from £3,500 including VAT (special offer until 30 November 2026). Up to 8 pages. Fixed proposal after a 30-minute discovery call.",
          url: "https://integrated.social/services/ai-websites/#pricing",
        },
        {
          "@type": "Offer",
          "@id":
            "https://integrated.social/services/ai-websites/#offer-ai-website-visibility-aeo",
          name: "AI Website Visibility (AEO)",
          price: "18500",
          priceCurrency: "GBP",
          priceSpecification: {
            "@type": "UnitPriceSpecification",
            price: "18500",
            priceCurrency: "GBP",
            valueAddedTaxIncluded: true,
          },
          description:
            "AI Website Visibility (AEO) from £18,500 including VAT. Up to 20 pages plus a blog system. Fixed proposal after a 30-minute discovery call.",
          url: "https://integrated.social/services/ai-websites/#pricing",
        },
        {
          "@type": "Offer",
          "@id":
            "https://integrated.social/services/ai-websites/#offer-enterprise-ai",
          name: "Enterprise AI",
          price: "38000",
          priceCurrency: "GBP",
          priceSpecification: {
            "@type": "UnitPriceSpecification",
            price: "38000",
            priceCurrency: "GBP",
            valueAddedTaxIncluded: true,
          },
          description:
            "Enterprise AI from £38,000 including VAT. Larger content systems. Fixed proposal after a 30-minute discovery call.",
          url: "https://integrated.social/services/ai-websites/#pricing",
        },
      ],
    },
    {
      "@type": "FAQPage",
      "@id": "https://integrated.social/services/ai-websites/#faq",
      mainEntity: faqs.map(({ q, a }) => ({
        "@type": "Question",
        name: q,
        acceptedAnswer: { "@type": "Answer", text: a },
      })),
    },
    {
      "@type": "BreadcrumbList",
      "@id": "https://integrated.social/services/ai-websites/#breadcrumb",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: "https://integrated.social/",
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Services",
          item: "https://integrated.social/services/",
        },
        {
          "@type": "ListItem",
          position: 3,
          name: "AI Websites",
          item: "https://integrated.social/services/ai-websites/",
        },
      ],
    },
  ],
};

export default function ServiceAIWebsites() {
  return (
    <>
      <SEOHead
        title="AI Website Design Agency London | AEO and SEO-Ready Sites"
        description={pageDescription}
        ogImage="https://integrated.social/media/ai-websites-service-hero_dfe5e872.png"
        canonical="https://integrated.social/services/ai-websites/"
      />
      <SchemaScript schema={aiWebsitesSchema} />
      <Header />
      <main>
        <div className="container px-6 pt-20">
          <Breadcrumbs
            includeSchema={false}
            items={[
              { label: "Services", href: "/services/" },
              { label: "AI Websites" },
            ]}
          />
        </div>

        <section className="border-b border-white/10 bg-[#0a0a0a] pb-16 pt-28">
          <div className="mx-auto grid max-w-7xl items-center gap-12 px-6 lg:grid-cols-2 lg:px-8">
            <div>
              <span className="mb-4 inline-block rounded-sm border border-[#FF8C00]/30 px-3 py-1 font-mono text-xs uppercase tracking-widest text-[#FF8C00]">
                AI Website Design · London
              </span>
              <h1 className="mb-5 font-serif text-4xl font-bold leading-tight text-white lg:text-5xl">
                AI Website Design Agency in London for B2B and B2C Brands
              </h1>
              <p
                data-answer-block
                className="mb-8 text-lg leading-relaxed text-white/70"
              >
                {answerBlock}
              </p>
              <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap">
                <Link
                  href="/free-ai-growth-audit/"
                  className="inline-flex items-center justify-center gap-2 bg-[#FF8C00] px-6 py-3.5 text-sm font-semibold text-black transition-colors hover:bg-[#FF8C00]/90"
                >
                  <Search className="h-4 w-4" />
                  Check my AI visibility
                </Link>
                <a href="/contact/?service=ai-websites#enquiry" data-enquiry-action="ai_websites_hero"
                  onClick={() => trackSectionCtaClick({ section_name: "service_ai_websites_hero", cta_text: "Send enquiry", destination: "/contact/?service=ai-websites#enquiry" })}
                  className="inline-flex min-h-11 items-center justify-center gap-2 border border-white/55 px-6 py-3.5 text-sm font-semibold text-white hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#FFB347]">Send enquiry <ArrowRight className="h-4 w-4" aria-hidden="true" /></a>
                <a
                  aria-label="Book a 30-minute discovery call with Integrated.Social"
                  href="https://calendly.com/modielnadi/30min"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 border border-[#FF8C00]/40 px-6 py-3.5 text-sm text-[#FF8C00] transition-colors hover:bg-[#FF8C00]/10"
                >
                  <ArrowRight className="h-4 w-4" />
                  Ask for a fixed-price proposal
                </a>
              </div>
            </div>
            <img
              src="/media/ai-websites-service-hero_dfe5e872.png"
              alt="AI website design planning board with answer-first pages, structured data, and review stages"
              width={1200}
              height={800}
              fetchPriority="high"
              className="w-full rounded-sm object-cover"
            />
          </div>
        </section>

        <section className="border-b border-white/10 bg-[#111] py-16">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <h2 className="mb-4 font-serif text-3xl font-bold text-white">
              What an AI-ready website includes
            </h2>
            <p className="mb-8 max-w-4xl leading-relaxed text-white/60">
              An AI-ready website is a well-built website with fewer obstacles
              between a question and a trustworthy answer. Important pages state
              their answer early, keep evidence beside the claim, and remain
              reachable without scripts that crawlers struggle to run.
              Structured data describes the business consistently on every page.
              Speed, accessibility, and clear internal links come next. None of
              it is a trick, and none of it guarantees that an assistant will
              cite you.
            </p>
            <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
              {[
                "Answer-first page structure: the question, a direct answer, then the detail and evidence.",
                "Server-rendered HTML and JSON-LD in the page head, so crawlers read the same content people do.",
                "Consistent Organization and Person entities, with clear canonicals and internal links.",
                "Core Web Vitals: LCP, CLS, and INP.",
                "An accurate XML sitemap and robots.txt that allows public search crawlers.",
                "Analytics, Search Console, and Bing Webmaster Tools set up before launch.",
              ].map(item => (
                <div
                  key={item}
                  className="border border-white/10 bg-white/[0.02] p-5 text-sm leading-relaxed text-white/65"
                >
                  <CheckCircle2 className="mb-3 h-5 w-5 text-[#FF8C00]" />
                  {item}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="border-b border-white/10 bg-[#0a0a0a] py-16">
          <div className="mx-auto grid max-w-7xl gap-10 px-6 lg:grid-cols-2 lg:px-8">
            <div>
              <h2 className="mb-4 font-serif text-3xl font-bold text-white">
                How we use AI agents and human sign-off
              </h2>
              <p className="leading-relaxed text-white/60">
                We use AI agents to research buyer questions, draft page copy,
                write components, and produce structured data. They are tools,
                not partners. A named senior person signs off every material
                claim, price, and page before release, because a fluent draft is
                not evidence. Every release goes through the same checks,
                whichever tool produced the first draft.
              </p>
            </div>
            <div className="border border-white/10 bg-white/[0.02] p-6">
              <h3 className="mb-3 font-semibold text-white">
                When a DIY AI website builder is enough
              </h3>
              <p className="mb-4 text-sm leading-relaxed text-white/60">
                A builder can be the better choice when you need a simple site
                quickly, work with a smaller budget, and have someone in-house
                who can verify every claim before publishing.
              </p>
            </div>
          </div>
        </section>

        <section
          id="pricing"
          className="border-b border-white/10 bg-[#111] py-20"
        >
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <h2 className="mb-4 font-serif text-3xl font-bold text-white">
              AI website pricing
            </h2>
            <p className="mb-10 max-w-4xl leading-relaxed text-white/60">
              Every build is priced as a fixed proposal after a 30-minute
              discovery call because scope, content readiness, and integrations
              vary. The tiers below are indicative starting points, not quotes.
              No retainer is required for the build itself. If you want ongoing
              optimization after launch, that is a separate program with its own
              scope. The Free AI Visibility Score gives you a baseline before
              you commit.
            </p>
            <div className="grid gap-6 md:grid-cols-3">
              {pricingTiers.map(tier => (
                <div
                  key={tier.name}
                  className="flex flex-col border border-white/10 bg-white/[0.02] p-8"
                >
                  <h3 className="mb-2 font-serif text-xl font-bold text-white">
                    {tier.name}
                  </h3>
                  <p className="mb-1 text-2xl font-bold text-[#FF8C00]">
                    {tier.price}
                  </p>
                  {tier.note && (
                    <p className="mb-4 text-xs text-[#FF8C00]/80">
                      {tier.note}
                    </p>
                  )}
                  <p className="mb-6 text-sm leading-relaxed text-white/60">
                    {tier.description}
                  </p>
                  <ul className="mb-8 space-y-3 text-sm text-white/70">
                    {tier.features.map(feature => (
                      <li key={feature} className="flex gap-2">
                        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#FF8C00]" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <Link
                    href={`/contact/?service=ai-website&tier=${tier.tier}`}
                    className="mt-auto inline-flex items-center justify-center gap-2 border border-white/20 px-5 py-3 text-sm font-semibold text-white transition-colors hover:border-[#FF8C00]/50 hover:text-[#FF8C00]"
                  >
                    Discuss this tier <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="border-b border-white/10 bg-[#0a0a0a] py-16">
          <div className="mx-auto grid max-w-7xl gap-10 px-6 lg:grid-cols-2 lg:px-8">
            <div>
              <h2 className="mb-4 font-serif text-3xl font-bold text-white">
                How we measure an AI website
              </h2>
              <p className="leading-relaxed text-white/60">
                We measure an AI website on business evidence, not screenshots.
                Before launch we agree a fixed panel of buyer questions and test
                them across the assistants your buyers use, logging the date,
                market, answer, and cited URL. After launch we track indexing,
                identifiable AI referrals, qualified inquiries, and what new
                customers say about how they found you. One observation is an
                anecdote; repeated observations show a pattern worth acting on.
              </p>
            </div>
            <div className="rounded-sm border border-white/10 bg-white/[0.02] p-6 text-sm leading-relaxed text-white/65">
              <p className="mb-3 font-semibold text-white">
                Useful evidence includes:
              </p>
              <ul className="space-y-3">
                <li>
                  Indexing and crawl coverage in Search Console and Bing
                  Webmaster Tools.
                </li>
                <li>
                  Identifiable AI referrals, reviewed alongside CRM inquiries.
                </li>
                <li>
                  A fixed buyer-question panel that is re-tested on an agreed
                  cadence.
                </li>
              </ul>
            </div>
          </div>
        </section>

        <section id="faq" className="border-b border-white/10 bg-[#111] py-20">
          <div className="mx-auto max-w-4xl px-6 lg:px-8">
            <span className="font-mono text-xs uppercase tracking-widest text-[#FF8C00]">
              FAQ
            </span>
            <h2 className="mb-4 mt-3 font-serif text-3xl font-bold text-white">
              AI website questions, answered
            </h2>
            <p data-faq-summary className="mb-10 leading-relaxed text-white/60">
              {faqSummary}
            </p>
            <FaqAccordion
              items={faqs}
              className="space-y-3"
              itemClassName="border border-white/10 bg-white/[0.02]"
              buttonClassName="w-full flex items-start justify-between gap-4 p-5 text-left"
              questionClassName="m-0"
              answerClassName="border-t border-white/10 px-5 pb-5 pt-4 text-sm text-white/60 leading-relaxed"
              iconClassName="w-4 h-4 text-white/40 shrink-0 mt-0.5 transition-transform duration-200"
            />
          </div>
        </section>

        <section className="bg-[#0a0a0a] py-20">
          <div className="mx-auto max-w-5xl px-6 text-center lg:px-8">
            <Globe className="mx-auto mb-5 h-9 w-9 text-[#FF8C00]" />
            <h2 className="mb-4 font-serif text-3xl font-bold text-white">
              Choose a build with a clear scope
            </h2>
            <p className="mx-auto mb-8 max-w-2xl leading-relaxed text-white/60">
              Start with your current site, your buyer questions, and the
              evidence you can publish. We will confirm the right scope and
              fixed price after a 30-minute discovery call.
            </p>
            <div className="flex flex-col justify-center gap-4 sm:flex-row">
              <Link
                href="/free-ai-growth-audit/"
                className="inline-flex items-center justify-center gap-2 bg-[#FF8C00] px-6 py-3.5 text-sm font-semibold text-black hover:bg-[#FF8C00]/90"
              >
                Check my AI visibility <ArrowRight className="h-4 w-4" />
              </Link>
              <a
                href="https://calendly.com/modielnadi/30min"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 border border-white/20 px-6 py-3.5 text-sm font-semibold text-white hover:border-[#FF8C00]/50 hover:text-[#FF8C00]"
              >
                Book a 30-minute discovery call <Shield className="h-4 w-4" />
              </a>
            </div>
          </div>
        </section>
      </main>
      <section
        className="mx-auto max-w-5xl px-4 pb-12"
        aria-label="Free AI Visibility Score"
      >
        <ScoreInlineForm placement="service_end" />
      </section>
      <Footer />
    </>
  );
}
